import { useState, useEffect, useCallback } from 'react';
import ChartCommentService, { type ChartComment, type SaveCommentParams } from '../services/ChartCommentService';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import { useSAPDataStore } from '../store/SAPDataStore';
import { isDemoMode } from '../services/demoMode';

interface UseChartCommentsOptions {
  chartType: string;
  machineBK?: string;
}

export function useChartComments({ chartType, machineBK }: UseChartCommentsOptions) {
  const { token } = useUnifiedAuth();
  const { dateRange } = useSAPDataStore();
  const [comments, setComments] = useState<ChartComment[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchComments = useCallback(async () => {
    // Comments are authored by real users against the real org. In demo mode
    // the affordance is hidden too (see the base charts), but gating the fetch
    // here is what stops one NATS request per chart per mount.
    if (isDemoMode()) return;
    // Skip if no chartType (comments not enabled) or missing required data
    if (!chartType || !token || !machineBK || !dateRange.start || !dateRange.end) return;
    setLoading(true);
    try {
      const data = await ChartCommentService.getComments(
        {
          chart_type: chartType,
          machine_bk: machineBK,
          date_from: `${dateRange.start}T00:00:00.000Z`,
          date_to: `${dateRange.end}T23:59:59.999Z`,
        },
        token,
      );
      setComments(data);
    } finally {
      setLoading(false);
    }
  }, [token, machineBK, dateRange.start, dateRange.end, chartType]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  // Also fetch when token becomes available (after page refresh)
  useEffect(() => {
    if (token && chartType && machineBK) {
      fetchComments();
    }
  }, [token]);

  const addComment = useCallback(
    async (params: Omit<SaveCommentParams, 'chart_type' | 'machine_bk'>) => {
      if (isDemoMode()) return null;
      if (!token || !machineBK) return null;
      const saved = await ChartCommentService.saveComment(
        { ...params, chart_type: chartType, machine_bk: machineBK },
        token,
      );
      if (saved) {
        setComments((prev) => [...prev, saved]);
      } else {
        // Backend may not return the comment - refetch to get latest
        await fetchComments();
      }
      return saved;
    },
    [token, machineBK, chartType, fetchComments],
  );

  const updateComment = useCallback(
    async (commentId: string, text: string) => {
      if (isDemoMode()) return null;
      if (!token || !machineBK) return null;
      const saved = await ChartCommentService.saveComment(
        { comment_id: commentId, chart_type: chartType, machine_bk: machineBK, text },
        token,
      );
      if (saved) {
        setComments((prev) => prev.map((c) => (c.id === commentId ? saved : c)));
      } else {
        // cockpitservice answers an edit without the comment: refetch, so the
        // panel shows the stored text (the new one, or the old one if the
        // edit failed).
        await fetchComments();
      }
      return saved;
    },
    [token, machineBK, chartType, fetchComments],
  );

  const deleteComment = useCallback(
    async (commentId: string) => {
      if (isDemoMode()) return false;
      if (!token) return false;
      const ok = await ChartCommentService.deleteComment({ comment_id: commentId }, token);
      if (ok) {
        setComments((prev) => prev.filter((c) => c.id !== commentId));
      }
      return ok;
    },
    [token],
  );

  return { comments, loading, addComment, updateComment, deleteComment, refetch: fetchComments };
}
